import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { Save, Loader2, Search } from 'lucide-react';

var API_URL = process.env.REACT_APP_BACKEND_URL;

export function NmiSettings({ token }) {
  var [enabled, setEnabled] = useState(false);
  var [apiUrl, setApiUrl] = useState('');
  var [apiKey, setApiKey] = useState('');
  var [provider, setProvider] = useState('custom');
  var [saving, setSaving] = useState(false);
  var [testNmi, setTestNmi] = useState('');
  var [testResult, setTestResult] = useState(null);
  var [testing, setTesting] = useState(false);

  useEffect(function() {
    axios.get(API_URL + '/api/settings/nmi-lookup?authorization=' + token).then(function(r) {
      setEnabled(r.data.enabled || false);
      setApiUrl(r.data.api_url || '');
      setApiKey(r.data.api_key || '');
      setProvider(r.data.provider || 'custom');
    }).catch(function() {});
  }, [token]);

  function saveSettings() {
    setSaving(true);
    var form = new FormData();
    form.append('enabled', enabled ? 'true' : 'false');
    form.append('api_url', apiUrl);
    form.append('api_key', apiKey);
    form.append('provider', provider);
    axios.post(API_URL + '/api/settings/nmi-lookup?authorization=' + token, form)
      .then(function() { toast.success('NMI lookup settings saved'); })
      .catch(function() { toast.error('Failed to save'); })
      .finally(function() { setSaving(false); });
  }

  function testLookup() {
    if (!testNmi) { toast.error('Enter an NMI to test'); return; }
    setTesting(true);
    setTestResult(null);
    axios.get(API_URL + '/api/public/nmi-lookup?nmi=' + encodeURIComponent(testNmi))
      .then(function(r) { setTestResult(r.data); })
      .catch(function() { toast.error('Lookup failed'); })
      .finally(function() { setTesting(false); });
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Search className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle>NMI Lookup Configuration</CardTitle>
              <CardDescription>Configure external NMI (MSATS) lookup API for automatic state and distributor detection</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center gap-3">
            <Switch checked={enabled} onCheckedChange={setEnabled} className="data-[state=unchecked]:bg-slate-300" data-testid="nmi-enabled-toggle" />
            <div>
              <Label className="text-sm font-semibold">Enable External API Lookup</Label>
              <p className="text-xs text-slate-500">When disabled, the system uses NMI prefix-based detection and bill address as fallback</p>
            </div>
          </div>

          {enabled && (
            <div className="space-y-4 pl-1 border-l-2 border-primary/20 ml-5">
              <div className="pl-4">
                <Label>API Endpoint URL *</Label>
                <Input value={apiUrl} onChange={function(e) { setApiUrl(e.target.value); }} placeholder="https://api.msats-provider.com/nmi-lookup" className="mt-1" data-testid="nmi-api-url" />
                <p className="text-xs text-slate-500 mt-1">The system will call: GET {'<url>'}?nmi={'<nmi_value>'}</p>
              </div>
              <div className="pl-4">
                <Label>API Key</Label>
                <Input value={apiKey} onChange={function(e) { setApiKey(e.target.value); }} placeholder="Enter API key" type="password" className="mt-1" data-testid="nmi-api-key" />
                <p className="text-xs text-slate-500 mt-1">Sent as Authorization: Bearer and X-API-Key headers</p>
              </div>
              <div className="pl-4">
                <Label>Provider</Label>
                <Input value={provider} onChange={function(e) { setProvider(e.target.value); }} placeholder="custom" className="mt-1" data-testid="nmi-provider" />
              </div>
            </div>
          )}

          <Button onClick={saveSettings} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="save-nmi-settings-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save NMI Settings
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Test NMI Lookup</CardTitle>
          <CardDescription>Enter an NMI to test the current lookup configuration</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-3">
            <Input value={testNmi} onChange={function(e) { setTestNmi(e.target.value); }} placeholder="e.g. 6102000001" className="flex-1" data-testid="nmi-test-input" />
            <Button onClick={testLookup} disabled={testing} variant="outline" data-testid="nmi-test-btn">
              {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
              Test
            </Button>
          </div>
          {testResult && (
            <div className={'p-3 rounded-lg border text-sm ' + (testResult.found ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200')} data-testid="nmi-test-result">
              <p className="font-medium">{testResult.found ? 'State Detected: ' + testResult.state : 'No state detected'}</p>
              <p className="text-xs text-slate-500 mt-1">Method: {testResult.method}{testResult.distributor ? ' | Distributor: ' + testResult.distributor : ''}</p>
            </div>
          )}
          <div className="text-xs text-slate-500 space-y-1">
            <p className="font-medium text-slate-700">Built-in prefix detection (always available):</p>
            <p>NSW: NB, NA, 41, 40, 43 | VIC: VB, VA, 61, 60 | QLD: QB, QA, 31, 30</p>
            <p>SA: SB, SA, 20, 21 | TAS: 80, 81 | WA: 70, 71</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
